import { useEffect, useRef, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { LinkSimple, UploadSimple, CircleNotch } from '@phosphor-icons/react';

/**
 * Toggle between pasting an image URL or uploading directly to Cloudinary.
 * - When Cloudinary is not configured on the backend, the Upload tab is
 *   disabled with a hint. URL paste remains fully functional.
 * - On successful upload, the resulting secure_url is written back via onChange.
 */
export default function ImageInputField({
  label,
  value,
  onChange,
  onUpload, // optional: fired with { url, public_id } after a successful Cloudinary upload
  placeholder = 'https://...',
  testIdInput,
  testIdUploadBtn,
  testIdUrlTab,
  testIdUploadTab,
  folder = 'products/',
}) {
  const [mode, setMode] = useState('url'); // 'url' | 'upload'
  const [cloudReady, setCloudReady] = useState(null); // null = unknown, true/false
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api
      .get('/admin/cloudinary/status')
      .then((r) => { if (alive) setCloudReady(!!r.data?.configured); })
      .catch(() => { if (alive) setCloudReady(false); });
    return () => { alive = false; };
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be 5 MB or smaller');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const { data: sig } = await api.get('/admin/cloudinary/signature', { params: { folder } });
      const form = new FormData();
      form.append('file', file);
      form.append('api_key', sig.api_key);
      form.append('timestamp', sig.timestamp);
      form.append('signature', sig.signature);
      form.append('folder', sig.folder);
      const url = `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`;
      const res = await fetch(url, { method: 'POST', body: form });
      const json = await res.json();
      if (!res.ok || !json.secure_url) {
        throw new Error(json?.error?.message || 'Upload failed');
      }
      onChange({ target: { value: json.secure_url } });
      if (typeof onUpload === 'function') {
        onUpload({ url: json.secure_url, public_id: json.public_id || '' });
      }
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const inputCls = 'w-full h-10 px-3 rounded-lg bg-white border border-stone-200 focus:border-olive-400 focus:outline-none text-sm';
  const uploadDisabled = cloudReady === false;

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-medium text-stone-600">{label}</label>
        <div className="inline-flex rounded-full bg-stone-100 p-0.5 text-xs">
          <button
            type="button"
            data-testid={testIdUrlTab}
            onClick={() => setMode('url')}
            className={`inline-flex items-center gap-1 h-7 px-3 rounded-full transition-colors ${mode === 'url' ? 'bg-white shadow text-stone-900' : 'text-stone-500 hover:text-stone-700'}`}
          >
            <LinkSimple size={12} /> URL
          </button>
          <button
            type="button"
            data-testid={testIdUploadTab}
            onClick={() => !uploadDisabled && setMode('upload')}
            disabled={uploadDisabled}
            title={uploadDisabled ? 'Cloudinary not configured on server' : 'Upload an image file'}
            className={`inline-flex items-center gap-1 h-7 px-3 rounded-full transition-colors ${mode === 'upload' ? 'bg-white shadow text-stone-900' : 'text-stone-500 hover:text-stone-700'} ${uploadDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <UploadSimple size={12} /> Upload
          </button>
        </div>
      </div>

      {mode === 'url' ? (
        <input
          data-testid={testIdInput}
          value={value || ''}
          onChange={onChange}
          className={inputCls}
          placeholder={placeholder}
        />
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleFile}
              disabled={uploading}
              data-testid={testIdUploadBtn}
              className="block w-full text-xs text-stone-600 file:mr-3 file:rounded-full file:border-0 file:bg-olive-600 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-white hover:file:bg-olive-700 file:cursor-pointer"
            />
            {uploading && <CircleNotch size={16} className="animate-spin text-olive-600" />}
          </div>
          {value && (
            <div className="flex items-center gap-2 text-xs text-stone-600">
              <img src={value} alt="" className="w-8 h-8 rounded object-cover bg-stone-100" />
              <span className="truncate" title={value}>{value}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
